import isEqual from 'lodash/isEqual';
import type { Ref } from 'vue';
import { computed } from 'vue';

import {
  FreightAllowance,
  Order,
  OrderStatus,
  Shipment,
  ShipmentStatus,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { isStatusEqualOrBefore } from '@/util';

export interface UseFreightAllowanceResponse {
  freightAllowanceInternal: Ref<FreightAllowance | undefined>;
  isFreightAllowanceEditable: Ref<boolean>;
  shouldShowFreightAllowance: Ref<boolean>;
}

export const useFreightAllowance = (
  freightAllowance: Ref<FreightAllowance | undefined> | undefined,
  emit: (
    event: 'update:freight-allowance',
    freightAllowance?: FreightAllowance,
  ) => void,
): UseFreightAllowanceResponse => {
  const { parentEntity } = useParentEntityStore();

  const freightAllowanceInternal = computed({
    get: () =>
      freightAllowance?.value
        ? new FreightAllowance(freightAllowance.value)
        : undefined,

    set: (newVal) => {
      if (!isEqual(newVal, freightAllowance?.value)) {
        const updatedFreightAllowance = newVal
          ? new FreightAllowance(newVal)
          : undefined;
        emit('update:freight-allowance', updatedFreightAllowance);
      }
    },
  });

  const shouldShowFreightAllowance = computed(
    () => !!freightAllowance?.value?.enabled,
  );

  const isFreightAllowanceEditable = computed(() => {
    if (parentEntity.value instanceof Shipment) {
      return (
        parentEntity.value.status !== ShipmentStatus.CANCELED &&
        parentEntity.value.status !== ShipmentStatus.COMPLETE
      );
    } else if (parentEntity.value instanceof Order) {
      return (
        parentEntity.value.status !== OrderStatus.CANCELED &&
        isStatusEqualOrBefore(parentEntity.value.status, OrderStatus.PENDING)
      );
    } else {
      return false;
    }
  });

  return {
    freightAllowanceInternal,
    isFreightAllowanceEditable,
    shouldShowFreightAllowance,
  };
};
